import { DndContext, PointerSensor, TouchSensor, rectIntersection, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis, restrictToWindowEdges, snapCenterToCursor } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import { Fragment, useState } from "react";

import { DragHandle, SortableItem, SortableOverlay } from "modules/NoteSuggestions/components";
import "modules/NoteSuggestions/components/SortableList/SortableList.scss";

import type { Active, Over, UniqueIdentifier } from "@dnd-kit/core";
import type { NoteSuggestion } from "models";
import type { ReactNode } from "react";

type BaseItem = {
  id: UniqueIdentifier;
};

export type SortableItems = NoteSuggestion & BaseItem;

interface SortableListProps {
  items: SortableItems[];
  onChange: (items: SortableItems[]) => void;
  renderItem: (item: SortableItems, active: Active | null) => ReactNode;
}

export const SortableList = ({ items, onChange, renderItem }: SortableListProps) => {
  const [active, setActive] = useState<Active | null>(null);
  const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 100 } }));

  const handleDragStart = (active: Active) => setActive(active);
  const handleOnDragEnd = (active: Active, over: Over | null) => {
    if (over && active.id !== over.id) {
      const activeIndex = items.findIndex(item => item.id === active.id);
      const overIndex = items.findIndex(item => item.id === over.id);
      onChange(arrayMove(items, activeIndex, overIndex));
    }
    setActive(null);
  };

  const handleDragCancel = () => setActive(null);
  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis, restrictToWindowEdges, snapCenterToCursor]}
      collisionDetection={rectIntersection}
      onDragStart={({ active }) => handleDragStart(active)}
      onDragEnd={({ active, over }) => handleOnDragEnd(active, over)}
      onDragCancel={handleDragCancel}
    >
      <>
        <SortableContext items={items}>
          <ul className="note-container">
            {items.map(item => (
              <Fragment key={item.id}>{renderItem(item, active)}</Fragment>
            ))}
          </ul>
        </SortableContext>
        <SortableOverlay />
      </>
    </DndContext>
  );
};

SortableList.Item = SortableItem;
SortableList.DragHandle = DragHandle;
